import { useEffect, useState } from 'react'

import { pushEvent } from '@/lib/analytics'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface UsePwaInstallPromptResult {
  isInstallable: boolean
  promptInstall: () => Promise<void>
}

export function usePwaInstallPrompt(): UsePwaInstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    null,
  )

  useEffect(() => {
    function handleBeforeInstallPrompt(beforeInstallPromptEvent: Event): void {
      beforeInstallPromptEvent.preventDefault()
      setDeferredPrompt(beforeInstallPromptEvent as BeforeInstallPromptEvent)
    }

    // beforeinstallprompt is Chromium-only and not part of lib.dom's WindowEventMap.
    window.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt as EventListener,
    )
    return () =>
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt as EventListener,
      )
  }, [])

  async function promptInstall(): Promise<void> {
    if (!deferredPrompt) return
    pushEvent('pwa_install_prompt_shown')
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    pushEvent(outcome === 'accepted' ? 'pwa_install_accepted' : 'pwa_install_dismissed')
    setDeferredPrompt(null)
  }

  return { isInstallable: deferredPrompt !== null, promptInstall }
}
